import React, { useEffect, useMemo, useState } from "react";
import { store } from "../../redux/oldStore.tsx";
import { toast } from "react-toastify";
import NhdNav from "../Layout/NhdNav";
import UsStateMap from "./UsStateMap";

/**
 * Draft Contests UX — not listed in main nav.
 * Region: International | US → US shows SVG map for state selection.
 */
export default function ContestsNewStyle() {
  const [ready, setReady] = useState(false);
  const [clients, setClients] = useState([]);
  const [user, setUser] = useState(null);
  const [region, setRegion] = useState(null); // 'us' | 'international' | null
  const [stateCode, setStateCode] = useState(null);
  const [stateName, setStateName] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    store.server
      .getApi("Contests/List")
      .then((r) => {
        if (r && r.Success && r.Value) {
          setClients((r.Value.clients || []).filter((c) => c.active));
          setUser(r.Value.user || null);
        } else {
          toast.error((r && r.Message) || "Could not load contests");
        }
        setReady(true);
      })
      .catch(() => {
        toast.error("Could not load contests");
        setReady(true);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const usStates = useMemo(() => {
    // Treat SA / international-ish codes as non-US for this draft
    const intlCodes = ["SA", "CHINA", "KOREA", "GU", "AS", "INTERNATIONAL"];
    return clients.filter((c) => {
      const s = (c.state || "").toUpperCase();
      if (!s) return false;
      if (intlCodes.indexOf(s) > -1) return false;
      // 2-letter US state/territory codes
      return s.length <= 3;
    });
  }, [clients]);

  const international = useMemo(() => {
    const intlCodes = ["SA", "CHINA", "KOREA"];
    return clients.filter((c) => {
      const s = (c.state || "").toUpperCase();
      const name = (c.stateName || "").toLowerCase();
      if (intlCodes.indexOf(s) > -1) return true;
      if (name.indexOf("international") > -1) return true;
      if (name.indexOf("china") > -1 || name.indexOf("korea") > -1)
        return true;
      if (name.indexOf("singapore") > -1) return true;
      // Non-US bindings without standard state
      if (!s || s.length > 3) return true;
      return false;
    });
  }, [clients]);

  const byState = useMemo(() => {
    if (!stateCode) return [];
    const code = stateCode.toUpperCase();
    return usStates.filter(
      (c) => (c.state || "").toUpperCase() === code
    );
  }, [usStates, stateCode]);

  const listForRegion = useMemo(() => {
    let list =
      region === "international"
        ? international
        : region === "us"
        ? stateCode
          ? byState
          : []
        : [];
    const q = (search || "").trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [c.name, c.binding, c.stateName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [region, international, byState, stateCode, search]);

  const onSelectState = (code, name) => {
    setStateCode(code);
    setStateName(name);
  };

  return (
    <div className="nhd-page contests-new-style">
      <NhdNav user={user} onAuthChange={load} />
      <div className="nhd-page-body">
        <div className="cns-banner">
          <span className="cns-draft-badge">Draft</span>
          <h1>Find your contest</h1>
          <p className="cns-sub">
            Choose a region, then pick your contest. This page is a design draft
            and is not linked from the main menu yet.
          </p>
        </div>

        <div className="cns-region-picker">
          <button
            type="button"
            className={`cns-region-card ${
              region === "international" ? "active" : ""
            }`}
            onClick={() => {
              setRegion("international");
              setStateCode(null);
              setStateName(null);
            }}
          >
            <i className="fad fa-globe"></i>
            <div className="cns-region-title">International</div>
            <div className="cns-region-desc">
              Contests outside the United States
            </div>
          </button>
          <button
            type="button"
            className={`cns-region-card ${region === "us" ? "active" : ""}`}
            onClick={() => setRegion("us")}
          >
            <i className="fad fa-flag-usa"></i>
            <div className="cns-region-title">United States</div>
            <div className="cns-region-desc">
              Select your state on the map
            </div>
          </button>
        </div>

        {region === "us" && (
          <div className="cns-map-panel">
            <h2>Select your state</h2>
            <UsStateMap selected={stateCode} onSelect={onSelectState} />
            {stateName && (
              <div className="cns-selected-state">
                Selected: <strong>{stateName}</strong> ({stateCode})
                <button
                  type="button"
                  className="btn-xs btn-secondary"
                  onClick={() => {
                    setStateCode(null);
                    setStateName(null);
                  }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}

        {region && (region === "international" || stateCode) && (
          <div className="cns-results">
            <div className="contests-search">
              <label htmlFor="cns-search">Filter contests</label>
              <input
                id="cns-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name…"
              />
            </div>
            {!ready && <div>Loading…</div>}
            {ready && listForRegion.length === 0 && (
              <div className="contests-empty">
                No contests found for this selection.
              </div>
            )}
            <div className="contest-list">
              {listForRegion.map((c) => (
                <div key={c.id || c.fmsClientIntId} className="contest-row">
                  <div className="contest-row-main">
                    {c.binding ? (
                      <a
                        href={`https://${c.binding}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contest-name-link"
                      >
                        {c.name}
                      </a>
                    ) : (
                      <span className="contest-name">{c.name}</span>
                    )}
                    {c.stateName && (
                      <span className="cns-state-tag">{c.stateName}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!region && (
          <div className="cns-hint">
            Select <strong>International</strong> or{" "}
            <strong>United States</strong> to continue.
          </div>
        )}
      </div>
    </div>
  );
}
